import { Column, Nullable } from '@synqo/frontend-component-library';

export type TableColumnSetting = {
    size?: number;
    hidden?: boolean;
};

export type TableColumnSettings = Record<string, TableColumnSetting>;

type CalcColumnsLayoutsReturnType = {
    columnSizing: Record<string, number>;
    columnVisibility: Record<string, boolean>;
};

/**
 * Performs a shallow comparison between two records (objects).
 * It checks if both records have the same number of keys and if the
 * value for each key is strictly equal.
 * @returns `true` if the records are equal, otherwise `false`.
 */
export const areRecordsEqual = <T extends number | boolean>(
    recordA: Record<string, T>,
    recordB: Record<string, T>,
): boolean => {
    const keysA = Object.keys(recordA);
    const keysB = Object.keys(recordB);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) if (recordA[key] !== recordB[key]) return false;

    return true;
};

/**
 * Reconciles the current and desired column layout states.
 * It derives the desired state from column definitions and user settings, compares it
 * to the current state, and returns the original state objects if no changes are detected
 * to prevent unnecessary re-renders in React.
 *
 * @returns An object containing the new or existing state for sizing and visibility.
 */
export const reconcileColumnLayouts = <T>(
    tableSettings: Nullable<TableColumnSettings>,
    tableColumns: Column<T>[],
    currentSizing: Record<string, number>,
    currentVisibility: Record<string, boolean>,
): CalcColumnsLayoutsReturnType => {
    const nextSizing: Record<string, number> = {};
    const nextVisibility: Record<string, boolean> = {};

    for (const col of tableColumns) {
        const setting = tableSettings?.[col.name];
        const targetSize = setting?.size ?? col.size;
        const targetVisible = !setting?.hidden;

        if (targetSize !== undefined) nextSizing[col.name] = targetSize;

        nextVisibility[col.name] = targetVisible;
    }

    const sizingHasNotChanged = areRecordsEqual(nextSizing, currentSizing);
    const visibilityHasNotChanged = areRecordsEqual(nextVisibility, currentVisibility);

    return {
        columnSizing: sizingHasNotChanged ? currentSizing : nextSizing,
        columnVisibility: visibilityHasNotChanged ? currentVisibility : nextVisibility,
    };
};

/**
 * Extracts only the column settings that deviate from the default column definitions.
 * This creates a minimal payload for saving user-specific customizations.
 *
 * @returns A record of custom settings, or `null` if no customizations exist.
 */
export const extractCustomSettings = <T>(
    tableColumns: Column<T>[],
    columnSizing: Record<string, number>,
    columnVisibility: Record<string, boolean>,
): TableColumnSettings | null => {
    const customSettings: TableColumnSettings = {};
    let hasCustomizations = false;

    for (const col of tableColumns) {
        const hasCustomSize = col.name in columnSizing && columnSizing[col.name] !== col.size;

        const isHidden = col.name in columnVisibility && !columnVisibility[col.name];

        if (!hasCustomSize && !isHidden) continue;

        hasCustomizations = true;

        customSettings[col.name] = {
            ...(hasCustomSize && { size: columnSizing[col.name] }),
            ...(isHidden && { hidden: true }),
        };
    }

    return hasCustomizations ? customSettings : null;
};
